import React, { useState, useEffect, useRef } from 'react';
import { callApi } from '../api/api';
import DriverMapRoute from '../components/DriverMapRoute';

const DriverDashboard = ({ showMessage }) => {
    const [orders, setOrders] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMsg, setErrorMsg] = useState('');
    const [driverLocation, setDriverLocation] = useState(null);
    const watchIdRef = useRef(null);
    const latestOrderIdRef = useRef(null);
    const [activeRouteOrderId, setActiveRouteOrderId] = useState(null);

    const playAlarm = () => {
        try {
            const audio = new Audio('https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3');
            audio.play().catch(e => {
                console.log("Audio play failed, falling back to beep", e);
                const context = new (window.AudioContext || window.webkitAudioContext)();
                const oscillator = context.createOscillator();
                const gainNode = context.createGain();
                oscillator.type = 'sine';
                oscillator.frequency.setValueAtTime(880, context.currentTime);
                gainNode.gain.setValueAtTime(0.5, context.currentTime);
                oscillator.connect(gainNode);
                gainNode.connect(context.destination);
                oscillator.start();
                setTimeout(() => { oscillator.frequency.setValueAtTime(1046.50, context.currentTime); }, 150);
                setTimeout(() => { oscillator.stop(); }, 400);
            });
        } catch (e) {
            console.log("Audio alarm failed to play", e);
        }
    };

    // Hardcoded Store Location (Could also be fetched from backend config)
    const storeLocation = { lat: 19.0760, lng: 72.8777 }; // Example: Mumbai

    const fetchDriverOrders = async (silent = false) => {
        if (!silent) setIsLoading(true);
        setErrorMsg('');
        try {
            const res = await callApi('/orders/driver/assigned');
            if (res && res.success) {
                const fetchedOrders = res.orders || [];
                setOrders(fetchedOrders);
                
                if (fetchedOrders.length > 0) {
                    const currentTopId = fetchedOrders[0]._id;
                    if (silent && latestOrderIdRef.current && latestOrderIdRef.current !== currentTopId) {
                        playAlarm();
                        if (showMessage) showMessage('New assigned delivery!', 'success');
                    }
                    latestOrderIdRef.current = currentTopId;
                }
            } else if (Array.isArray(res)) {
                // In case backend just returns array
                setOrders(res);
                if (res.length > 0) {
                    const currentTopId = res[0]._id;
                    if (silent && latestOrderIdRef.current && latestOrderIdRef.current !== currentTopId) {
                        playAlarm();
                        if (showMessage) showMessage('New assigned delivery!', 'success');
                    }
                    latestOrderIdRef.current = currentTopId;
                }
            }
        } catch (error) {
            console.error('Failed to fetch assigned orders', error);
            setErrorMsg(error.message || 'Failed to load deliveries. Are you logged in as a driver?');
        } finally {
            if (!silent) setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchDriverOrders();
        
        // Poll every 10 seconds for instant updates
        const interval = setInterval(() => {
            fetchDriverOrders(true); // silent fetch
        }, 10000);
        
        return () => clearInterval(interval);
    }, []);

    // Watch driver location if there's an active "Out for Delivery" or "Shipped" order
    useEffect(() => {
        const outForDeliveryOrders = orders.filter(o => o.status === 'Out for Delivery' || o.status === 'Shipped');
        if (outForDeliveryOrders.length > 0) {
            if (!watchIdRef.current && 'geolocation' in navigator) {
                watchIdRef.current = navigator.geolocation.watchPosition(
                    async (position) => {
                        const { latitude, longitude } = position.coords;
                        setDriverLocation({ lat: latitude, lng: longitude });
                        
                        // Send update to backend for the first active order
                        const activeOrder = outForDeliveryOrders[0];
                        try {
                            await callApi(`/orders/driver/${activeOrder._id}/location`, 'PUT', { lat: latitude, lng: longitude });
                        } catch (err) {
                            console.error('Failed to update live location on server');
                        }
                    },
                    (err) => console.error('Geolocation error:', err),
                    { enableHighAccuracy: true, maximumAge: 10000, timeout: 5000 }
                );
            }
        } else {
            // Stop watching if no active orders
            if (watchIdRef.current) {
                navigator.geolocation.clearWatch(watchIdRef.current);
                watchIdRef.current = null;
            }
        }

        return () => {
            if (watchIdRef.current) {
                navigator.geolocation.clearWatch(watchIdRef.current);
                watchIdRef.current = null;
            }
        };
    }, [orders]);

    const updateOrderStatus = async (orderId, newStatus) => {
        try {
            const res = await callApi(`/orders/driver/${orderId}/status`, 'PUT', { status: newStatus });
            if (res.success || res.orderId) {
                showMessage(`Order marked as ${newStatus}`, 'success');
                fetchDriverOrders();
            } else {
                showMessage(res.message || 'Failed to update order.', 'error');
            }
        } catch (error) {
            showMessage('Error updating order.', 'error');
        }
    };

    const handleNavigate = (address) => {
        const url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
        window.open(url, '_blank');
    };

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[50vh]">
                <i className="fas fa-circle-notch fa-spin text-4xl text-sky-500 mb-4"></i>
                <p className="text-slate-500 font-medium">Loading assignments...</p>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-8 animate-fade-in max-w-4xl">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h2 className="text-3xl font-extrabold text-slate-800">Driver Dashboard</h2>
                    <p className="text-slate-500 font-medium mt-1">Manage your active deliveries</p>
                </div>
                <button 
                    onClick={() => fetchDriverOrders(false)}
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-white text-sky-600 hover:bg-sky-50 shadow-sm border border-slate-200 transition-colors"
                >
                    <i className="fas fa-sync-alt"></i>
                </button>
            </div>

            {errorMsg && (
                <div className="bg-red-50 text-red-700 p-4 rounded-xl mb-6 font-bold flex items-center shadow-sm">
                    <i className="fas fa-exclamation-triangle mr-3 text-red-500"></i>
                    {errorMsg}
                </div>
            )}

            {!errorMsg && orders.length === 0 ? (
                <div className="bg-white rounded-3xl p-12 text-center border border-slate-100 shadow-sm">
                    <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="fas fa-box-open text-3xl text-slate-300"></i>
                    </div>
                    <h3 className="text-xl font-bold text-slate-700 mb-2">No Active Deliveries</h3>
                    <p className="text-slate-500">You don't have any pending assignments right now. Take a break!</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {orders.map(order => (
                        <div key={order._id} className="bg-white rounded-3xl p-6 border border-slate-100 shadow-sm hover:shadow-md transition-shadow">
                            <div className="flex flex-col md:flex-row justify-between gap-4">
                                <div>
                                    <div className="flex items-center gap-3 mb-3">
                                        <span className="font-mono text-xs font-bold text-slate-600 bg-slate-100 px-2 py-1 rounded-md">{order.orderId}</span>
                                        <span className={`text-[10px] font-extrabold uppercase tracking-wider px-2 py-1 rounded-md ${
                                            (order.status === 'Out for Delivery' || order.status === 'Shipped') ? 'bg-amber-100 text-amber-700' : 'bg-sky-100 text-sky-700'
                                        }`}>
                                            {order.status}
                                        </span>
                                    </div>
                                    <h4 className="text-lg font-bold text-slate-800 mb-1">{order.customerName || 'Customer'}</h4>
                                    <p className="text-sm font-medium text-slate-600 mb-4 flex items-start gap-2 max-w-sm">
                                        <i className="fas fa-map-marker-alt text-sky-500 mt-1"></i>
                                        <span>{order.deliveryAddress}</span>
                                    </p>
                                    
                                    <div className="flex gap-4">
                                        <a href={`tel:${order.customerPhone}`} className="text-sm font-bold text-sky-600 hover:text-sky-700 transition-colors flex items-center gap-1.5">
                                            <i className="fas fa-phone"></i> Call Customer
                                        </a>
                                        <button 
                                            onClick={() => handleNavigate(order.deliveryAddress)}
                                            className="text-sm font-bold text-emerald-600 hover:text-emerald-700 transition-colors flex items-center gap-1.5"
                                        >
                                            <i className="fas fa-directions"></i> Navigate
                                        </button>
                                    </div>
                                </div>
                                
                                <div className="flex flex-col justify-end md:items-end gap-2 md:border-l border-slate-100 md:pl-6 pt-4 md:pt-0 border-t md:border-t-0">
                                    {(order.status !== 'Out for Delivery' && order.status !== 'Shipped') && (
                                        <button 
                                            onClick={() => updateOrderStatus(order._id, 'Out for Delivery')}
                                            className="w-full md:w-auto px-6 py-2.5 bg-amber-500 hover:bg-amber-600 text-white font-extrabold rounded-xl transition-all shadow-md shadow-amber-500/20"
                                        >
                                            <i className="fas fa-motorcycle mr-2"></i> Start Delivery
                                        </button>
                                    )}
                                    <button 
                                        onClick={() => updateOrderStatus(order._id, 'Delivered')}
                                        className="w-full md:w-auto px-6 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white font-extrabold rounded-xl transition-all shadow-md shadow-emerald-500/20"
                                    >
                                        <i className="fas fa-check-circle mr-2"></i> Mark Delivered
                                    </button>
                                </div>
                            </div>
                            
                            {/* Map UI Toggle */}
                            {(order.status === 'Out for Delivery' || order.status === 'Shipped') && (
                                <div className="mt-4 pt-4 border-t border-slate-100">
                                    <button 
                                        onClick={() => setActiveRouteOrderId(activeRouteOrderId === order._id ? null : order._id)}
                                        className="text-sky-600 font-bold hover:text-sky-700 flex items-center gap-2"
                                    >
                                        <i className={`fas fa-chevron-${activeRouteOrderId === order._id ? 'up' : 'down'}`}></i>
                                        {activeRouteOrderId === order._id ? 'Hide Live Map' : 'View Live Map'}
                                    </button>
                                    
                                    {activeRouteOrderId === order._id && (
                                        <div className="mt-4">
                                            <DriverMapRoute 
                                                storeLocation={storeLocation}
                                                customerAddress={order.deliveryAddress}
                                                driverLocation={driverLocation}
                                            />
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default DriverDashboard;
